/**
 * <ax-countdown> — compte à rebours du bloc « Ax compteur avec texte ».
 *
 * Deux modes (attribut `data-countdown-mode`) :
 * - `date`   : décompte vers `data-target-date`.
 * - `launch` : décompte vers `data-start-date` + durée (`data-duration-*`),
 *              pour les offres « X heures à partir du lancement ».
 *
 * Formats de date acceptés : `MM-DD-YYYY HH:MM:SS` (héritage de l'ancien thème),
 * `DD-MM-YYYY HH:MM:SS` (repli si le mois est > 12) et ISO `YYYY-MM-DD HH:MM[:SS]`.
 *
 * À la fin du décompte, le timer est masqué et le message de fin (rendu côté
 * serveur, `ref` data-countdown-end) est affiché — l'état « Compteur Off ».
 *
 * L'attribut `loaded` n'est posé qu'au premier affichage calculé : les groupes
 * restent à opacity 0 tant que les zéros initiaux n'ont pas été remplacés.
 */
class AxCountdown extends HTMLElement {
  #timer = null;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
  }

  #init() {
    const endDate = this.#resolveEndDate();
    if (Number.isNaN(endDate)) return;

    const tick = () => {
      const timeLeft = endDate - Date.now();

      if (timeLeft > 0) {
        this.#render(timeLeft);
        this.setAttribute('loaded', '');
        return;
      }

      clearInterval(this.#timer);
      this.#finish();
    };

    tick();
    if (!this.hasAttribute('finished')) {
      this.#timer = setInterval(tick, 1000);
    }
  }

  /** @returns {number} timestamp de fin (NaN si les réglages sont invalides) */
  #resolveEndDate() {
    if (this.dataset.countdownMode === 'launch') {
      const start = this.#parseDate(this.dataset.startDate);
      if (Number.isNaN(start)) return NaN;

      const days = parseInt(this.dataset.durationDays || '0', 10);
      const hours = parseInt(this.dataset.durationHours || '0', 10);
      const minutes = parseInt(this.dataset.durationMinutes || '0', 10);
      const seconds = parseInt(this.dataset.durationSeconds || '0', 10);

      return start + (((days * 24 + hours) * 60 + minutes) * 60 + seconds) * 1000;
    }

    return this.#parseDate(this.dataset.targetDate);
  }

  /**
   * @param {string} raw - Date saisie dans l'éditeur.
   * @returns {number} timestamp (NaN si illisible)
   */
  #parseDate(raw) {
    if (!raw) return NaN;
    const input = raw.trim();

    // ISO (YYYY-MM-DD [HH:MM[:SS]]) — le `T` rend le parsing fiable partout (Safari inclus).
    if (/^\d{4}-\d{2}-\d{2}/.test(input)) {
      return new Date(input.replace(' ', 'T')).getTime();
    }

    // Héritage ancien thème : MM-DD-YYYY HH:MM:SS, puis repli DD-MM-YYYY.
    const legacy = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;
    const us = input.replace(legacy, '$3-$1-$2T$4:$5:$6');
    let time = new Date(us).getTime();

    if (Number.isNaN(time)) {
      const fr = input.replace(legacy, '$3-$2-$1T$4:$5:$6');
      time = new Date(fr).getTime();
    }

    return time;
  }

  /** @param {number} timeLeft - Temps restant en millisecondes. */
  #render(timeLeft) {
    const showDays = this.dataset.showDays === 'true';
    const totalSeconds = Math.floor(timeLeft / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);

    const units = {
      days: showDays ? Math.floor(totalHours / 24) : null,
      // Sans les jours, les heures portent le total (potentiellement > 99).
      hours: showDays ? totalHours % 24 : totalHours,
      minutes: totalMinutes % 60,
      seconds: totalSeconds % 60,
    };

    for (const [unit, value] of Object.entries(units)) {
      if (value === null) continue;
      const container = this.querySelector(`[data-time="${unit}"]`);
      if (!container) continue;

      const digits = String(value).padStart(2, '0');
      // Reconstruit uniquement si le nombre de chiffres change (> 99 h possible).
      if (container.children.length !== digits.length) {
        container.replaceChildren(
          ...[...digits].map(() => {
            const span = document.createElement('span');
            span.className = 'ax-countdown__digit';
            return span;
          })
        );
      }
      [...digits].forEach((char, i) => {
        if (container.children[i].textContent !== char) {
          container.children[i].textContent = char;
        }
      });
    }
  }

  #finish() {
    this.setAttribute('finished', '');
    this.setAttribute('loaded', '');

    const timer = this.querySelector('[data-countdown-timer]');
    const endMessage = this.querySelector('[data-countdown-end]');

    if (endMessage) {
      timer?.setAttribute('hidden', '');
      endMessage.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('ax-countdown')) {
  customElements.define('ax-countdown', AxCountdown);
}
